/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Drawer, Select, Input, Tooltip, DatePicker, message } from 'antd';
import {
	RedoOutlined,
	PlusOutlined,
	CloseOutlined,
	DownloadOutlined,
	CheckOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { RCTable } from 'components';
import { ACL, FilterTitle } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import { Box, Button, Flex, Text } from 'atoms';
import { Tabs } from 'atoms/Tabs';
import { ClientListQueries, initialPaginateConfig, VendorInvoiceListTableConfig } from 'utils';
import { getUIVersion } from 'utils/router-static-data';
import { useAuth, useQueryParams } from 'hooks';
import { exportInvoice, getListAction, getTripFilterList } from 'store/pageSlice';
import { getFilterListV2 } from 'store/financeSliceV2';
import { searchVendorBillsV2, exportVendorBillCSVV2 } from 'store/vendorBillSliceV2';
import { bulkCreateInvoicePaymentV2 } from 'store/vendorBillApprovalSlice';
import { FilterAtnButton } from 'utils/commonStyled';
import ExportCustomCalender from './components/ExportCustomCalender';
import BulkPaymentModal from './components/BulkPaymentModal';
import BulkPaymentUploadModal from './components/BulkPaymentUploadModal';

const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const allOption = {
	value: '',
	label: 'All',
};

function populateOptions(data = [], showAll = true) {
	/**
	 * assign id & value to option list
	 */
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	/**
	 * Add All option of array first
	 */
	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

const ButtonWrapper = styled(Button)`
	color: #091744;
	background-color: transparent;
	align-self: center;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: auto;
	white-space: nowrap;
	padding: 6px 12px;
	margin-left: 0;
	&:hover {
		text-decoration: none;
		background-color: rgba(9, 23, 68, 0.04);
	}
	svg {
		margin-right: 8px;
	}
`;
// eslint-disable-next-line sonarjs/cognitive-complexity
export default function VendorInvoiceList() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { RangePicker } = DatePicker;
	const uiVersion = getUIVersion();
	const useV2API = uiVersion === 'new';
	const authData = useAuth();
	const bulkPayPermissions = useV2API ? authData?.v2_permissions : authData?.permissions;
	const canCreateBulkPayment =
		bulkPayPermissions?.create?.includes('bulk_invoice_payment') ||
		bulkPayPermissions?.create?.includes('all');
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [vendorsList, setVendorsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [open, setOpen] = useState(false);
	const [rowData, setRowData] = useState({});
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'vendor_bill',
		filterQuery: ClientListQueries,
		persistKey: 'vendorInvoiceList:filters',
	});
	const [exportModalVisible, setExportModalVisible] = useState(false);
	const [activeTab, setActiveTab] = useState('all');
	const [searchValue, setSearchValue] = useState('');
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [bulkModalVisible, setBulkModalVisible] = useState(false);
	const [bulkLoading, setBulkLoading] = useState(false);
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	useEffect(() => {
		const action = useV2API
			? searchVendorBillsV2(paginateConfig)
			: getListAction(paginateConfig);

		dispatch(action).then((res) => {
			let { cities = [], clients = [], projects = [], vendors = [] } = res;

			// For old UI (v1) we still rely on /search filters for dropdowns
			if (!useV2API) {
				cities = cities?.map((element) => ({
					value: element,
					label: element,
				}));
				cities.unshift(allOption);

				clients = populateOptions(clients);
				projects = populateOptions(projects);
				vendors = populateOptions(vendors);

				setCitiesList(cities);
				setClientsList(clients);
				setProjectsList(projects);
				setVendorsList(vendors);
			}
		});
	}, [dispatch, paginateConfig, useV2API]);

	// Fetch business units
	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			// Don't add "All" option for multiple select filters
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch]);

	// Load city list using V1 city API even in new UI
	useEffect(() => {
		dispatch(getTripFilterList('city'))
			.then((res) => {
				const rows = res?.rows || res?.data || {};
				const cityValues = rows?.cities || rows?.data?.cities || [];
				const cities = cityValues.map((element) => ({
					value: element,
					label: element,
				}));
				cities.unshift(allOption);
				setCitiesList(cities);
			})
			.catch((error) => {
				// Fallback to just "All" if city API fails
				console.error('Error fetching city list (V1):', error);
				setCitiesList([allOption]);
			});
	}, [dispatch]);

	// Helper to get label for V2 filter options with minimal branching
	const getV2FilterLabel = (filter, el) => {
		const baseId = el?.id || el?.value;

		const labelFieldsByFilter = {
			client: ['label', 'name', 'client_name'],
			project: ['project_name', 'label', 'name', 'project_code'],
			vendor: ['label', 'name', 'vendor_name'],
		};

		const prefixByFilter = {
			client: 'Client',
			project: 'Project',
			vendor: 'Vendor',
		};

		const fields = labelFieldsByFilter[filter] || labelFieldsByFilter.vendor;
		const prefix = prefixByFilter[filter] || prefixByFilter.vendor;

		for (let i = 0; i < fields.length; i += 1) {
			const field = fields[i];
			if (el?.[field]) {
				return el[field];
			}
		}

		return `${prefix} ${baseId}`;
	};

	// Helper to load a single V2 filter type
	const loadV2Filter = (filter, setter, showAll = true) => {
		dispatch(getFilterListV2({ filter }))
			.then((res) => {
				const rows = res?.rows || [];
				if (!Array.isArray(rows)) {
					setter(showAll ? [allOption] : []);
					return;
				}

				const options = rows.map((el) => ({
					value: el?.id || el?.value,
					label: getV2FilterLabel(filter, el),
				}));
				if (showAll) {
					options.unshift(allOption);
				}
				setter(options);
			})
			.catch((error) => {
				console.error(`Error fetching ${filter} filter (V2):`, error);
				setter(showAll ? [allOption] : []);
			});
	};

	// Load client, project, vendor lists using V2 list_search API (new UI)
	useEffect(() => {
		if (!useV2API) return;

		loadV2Filter('client', setClientsList, true);
		loadV2Filter('project', setProjectsList, true);
		loadV2Filter('vendor', setVendorsList, true);
	}, [dispatch, useV2API]);

	useEffect(() => {
		setSearchValue(paginateConfig?.query_str || '');
	}, [paginateConfig?.query_str]);

	useEffect(() => {
		if (activeTab === 'pending') {
			setpaginateConfig((prev) => ({
				...prev,
				pending_approval: true,
				offset: 0,
				currentPage: 1,
			}));
		} else {
			setpaginateConfig((prev) => {
				const { pending_approval, ...rest } = prev;
				return {
					...rest,
					offset: 0,
					currentPage: 1,
				};
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	// business_unit_ids is now always an array thanks to useQueryParams transformation
	const businessUnitIds = paginateConfig?.business_unit_ids || [];

	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const UserVal = paginateConfig?.user_type ? 'User Type' : '';
	const cityFilterValue = paginateConfig?.city ? 'City' : '';
	const dateFilterValue = paginateConfig?.from_date || paginateConfig?.to_date ? 'Date' : '';
	const client = paginateConfig?.client_id ? 'Client' : '';
	const project = paginateConfig?.project_id ? 'Project' : '';
	const businessUnitFilterValue = businessUnitIds.length > 0 ? 'Business Unit' : '';
	const StatusValueAll = paginateConfig?.is_active === '' ? 'Status' : '';
	const UserAllVal = paginateConfig?.user_type === '' ? 'User Type' : '';
	const clientAll = paginateConfig?.client_id === '' ? 'Client' : '';
	const projectAll = paginateConfig?.project_id === '' ? 'Project' : '';
	const cityAllValue = paginateConfig?.city === '' ? 'City' : '';
	const vendorValue = paginateConfig?.vendor_id === '' ? 'Vendor' : '';
	const VendorAllValue = paginateConfig?.vendor_id ? 'Vendor' : '';
	const paymentStatusFilterValue = paginateConfig?.payment_status ? 'Payment Status' : '';

	const paginationArry = [
		cityFilterValue,
		StatusValue,
		UserVal,
		client,
		project,
		businessUnitFilterValue,
		projectAll,
		StatusValueAll,
		UserAllVal,
		clientAll,
		cityAllValue,
		VendorAllValue,
		vendorValue,
		dateFilterValue,
		paymentStatusFilterValue,
	];
	const validArray = paginationArry.filter((n) => n);
	console.log(validArray.length);

	const exportfunc = (toDate, fromDate, includeTrips = false) => {
		const payload = {
			report_type: useV2API ? 'vendor_bill_export' : 'vendor_bill_csv_export',
			file_type: 'csv',
			data: {
				from_date: fromDate,
				to_date: toDate,
				city: paginateConfig?.city,
				project_id: paginateConfig?.project_id,
				vendor_id: paginateConfig?.vendor_id,
				client_id: paginateConfig?.client_id,
				trip_type: paginateConfig?.trip_type,
				trip_status: paginateConfig?.trip_status,
				include_trips: includeTrips,
				url_type: 'download',
				...(activeTab === 'pending' ? { pending_approval: true } : {}),
			},
		};

		const action = useV2API
			? exportVendorBillCSVV2({ data: payload })
			: exportInvoice({ data: payload });

		dispatch(action).then((res) => {
			console.log('res', res);
		});
	};
	// eslint-disable-next-line sonarjs/cognitive-complexity
	const handleExportClick = () => {
		setExportModalVisible(true);
	};

	function getFormattedDate(val) {
		return moment(val).startOf('day').format('MM/DD/YYYY HH:mm');
	}

	function getFormattedEndDate(val) {
		return moment(val).endOf('day').format('MM/DD/YYYY HH:mm');
	}

	// Bulk "mark as paid" is available in the new UI on the main invoice list
	const canBulkPay = useV2API && activeTab === 'all' && canCreateBulkPayment;

	const isRowPayable = (row) =>
		String(row?.invoice_status || '').toLowerCase() === 'pending' &&
		row?.has_pending_changes === false;

	// Clear selection whenever the tab changes or the underlying rows reload.
	useEffect(() => {
		setSelectedRowKeys([]);
	}, [activeTab, rowData?.rows]);

	const rowSelection = canBulkPay
		? {
				selectedRowKeys,
				preserveSelectedRowKeys: false,
				onChange: (keys) => setSelectedRowKeys(keys),
				getCheckboxProps: (record) => ({
					disabled: !isRowPayable(record),
				}),
		  }
		: undefined;

	// Remaining balance to be paid for a row = net payable (Total Payable incl. GST) × pending %.
	// Uses total_payable_amount — the SAME base the backend/search use for the pending
	// percentage — NOT grand_total (which re-adds any advance already paid via UTR).
	const getRemainingAmount = (row) => {
		const netPayable = Number(row?.total_payable_amount ?? row?.grand_total ?? 0);
		const pendingPct = row?.pending_amount_percentage;
		if (pendingPct === undefined || pendingPct === null) {
			return Math.max(netPayable - Number(row?.paid_amount || 0), 0);
		}
		return Math.round(netPayable * (Number(pendingPct) / 100) * 100) / 100;
	};

	const selectedInvoices = (rowData?.rows || [])
		.filter((row) => selectedRowKeys.includes(row.id))
		.map((row) => ({
			id: row.id,
			vendor_bill_no: row.vendor_bill_no,
			amount: getRemainingAmount(row),
		}));

	const handleBulkPaySubmit = async ({ utr_number, paid_date }) => {
		setBulkLoading(true);
		try {
			const response = await dispatch(
				bulkCreateInvoicePaymentV2({
					vendor_bill_ids: selectedRowKeys,
					utr_number,
					paid_date,
				})
			);
			if (response?.status !== 'success') {
				// All-or-nothing failure. The thunk/API layer already surfaced the error
				// toast; throw so the modal keeps the entered UTR/date and stays open.
				throw new Error(response?.error?.message || 'Failed to mark invoices as paid');
			}
			const successCount = response?.data?.success_count ?? selectedRowKeys.length;
			message.success(`Successfully marked ${successCount} invoice(s) as paid`);
			setBulkModalVisible(false);
			setSelectedRowKeys([]);
			// Refresh the list by resetting pagination (triggers RCTable re-fetch).
			setpaginateConfig((prev) => ({
				...prev,
				offset: 0,
				currentPage: 1,
			}));
		} finally {
			setBulkLoading(false);
		}
	};

	return (
		<>
			<Box
				background="#FFFFFF"
				borderRadius="8px"
				p="24px"
				boxShadow="0 2px 8px rgba(0,0,0,0.04)"
				mb={4}
			>
				<Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								label: 'Vendor Invoice',
								key: 'all',
							},
							{
								label: 'Pending Vendor Invoice',
								key: 'pending',
							},
						]}
						tabBarStyle={{ borderBottom: 'none', marginBottom: 0 }}
					/>
				</Box>
				<Flex
					justifyContent="space-between"
					alignItems="center"
					mt={3}
					gap="16px"
					flexWrap="wrap"
				>
					<Flex alignItems="center" gap="10px" flex="1">
						<Box flex="0 0 20%" marginRight="10px" minWidth="200px">
							<LtSearchBar
								placeholder="Search by Bill/Invoice No."
								enterButton="Search"
								value={searchValue}
								allowClear
								onChange={(e) => setSearchValue(e.target.value)}
								onSearch={(val) => {
									setSearchValue(val || '');
									setpaginateConfig({
										...initialPaginateConfig,
										query_str: val,
										search_type: 'vendor_bill',
										...(activeTab === 'pending'
											? { pending_approval: true }
											: {}),
									});
								}}
							/>
						</Box>
						<FilterAtnButton onClick={() => setOpen(true)}>FILTERS</FilterAtnButton>
						<FilterDisplayBox>
							{StatusValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											is_active: undefined,
										}))
									}
								>
									{StatusValue}
								</FilterTag>
							)}
							{StatusValueAll && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											is_active: undefined,
										}))
									}
								>
									{StatusValueAll}
								</FilterTag>
							)}
							{projectAll && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											project_id: undefined,
										}))
									}
								>
									{projectAll}
								</FilterTag>
							)}
							{UserVal && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											user_type: undefined,
										}))
									}
								>
									{UserVal}
								</FilterTag>
							)}
							{cityFilterValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({ ...prev, city: undefined }))
									}
								>
									{cityFilterValue}
								</FilterTag>
							)}
							{dateFilterValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											from_date: undefined,
											to_date: undefined,
										}))
									}
								>
									{dateFilterValue}
								</FilterTag>
							)}
							{client && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											client_id: undefined,
										}))
									}
								>
									{client}
								</FilterTag>
							)}
							{project && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											project_id: undefined,
										}))
									}
								>
									{project}
								</FilterTag>
							)}
							{businessUnitFilterValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											business_unit_ids: [],
										}))
									}
								>
									{businessUnitFilterValue}
								</FilterTag>
							)}
							{UserAllVal && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											user_type: undefined,
										}))
									}
								>
									{UserAllVal}
								</FilterTag>
							)}
							{clientAll && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											client_id: undefined,
										}))
									}
								>
									{clientAll}
								</FilterTag>
							)}
							{cityAllValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({ ...prev, city: undefined }))
									}
								>
									{cityAllValue}
								</FilterTag>
							)}
							{VendorAllValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											vendor_id: undefined,
										}))
									}
								>
									{VendorAllValue}
								</FilterTag>
							)}
							{vendorValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											vendor_id: undefined,
										}))
									}
								>
									{vendorValue}
								</FilterTag>
							)}
							{paymentStatusFilterValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											payment_status: undefined,
										}))
									}
								>
									{paymentStatusFilterValue}
								</FilterTag>
							)}
							{validArray.length > 0 && (
								<Tooltip title="Clear all filters">
									<CloseOutlined
										style={{
											color: '#ff4d4f',
											cursor: 'pointer',
											marginLeft: 'auto',
											marginRight: '4px',
										}}
										onClick={() => {
											setSearchValue('');
											setpaginateConfig({
												...initialPaginateConfig,
												search_type: 'vendor_bill',
												...(activeTab === 'pending'
													? { pending_approval: true }
													: {}),
											});
										}}
									/>
								</Tooltip>
							)}
						</FilterDisplayBox>
					</Flex>

					<Flex alignItems="center" gap="4px" flexShrink={0}>
						{canBulkPay && (
							<ACL action="create" module="bulk_invoice_payment">
								<ButtonWrapper
									onClick={() => setBulkModalVisible(true)}
									icons={<CheckOutlined />}
									disabled={selectedRowKeys.length === 0}
								>
									MARK AS PAID
									{selectedRowKeys.length > 0
										? ` (${selectedRowKeys.length})`
										: ''}
								</ButtonWrapper>
							</ACL>
						)}
						{useV2API && activeTab === 'all' && (
							<ACL action="bulk_upload" module="invoice_payment">
								<Tooltip title="Upload payment sheet">
									<ButtonWrapper
										onClick={() => setBulkUploadModalVisible(true)}
										icons={<UploadOutlined />}
									>
										IMPORT
									</ButtonWrapper>
								</Tooltip>
							</ACL>
						)}
						<ButtonWrapper onClick={handleExportClick} icons={<DownloadOutlined />}>
							EXPORT
						</ButtonWrapper>
						<ButtonWrapper
							onClick={() =>
								setpaginateConfig({
									...initialPaginateConfig,
									search_type: 'vendor_bill',
									...(activeTab === 'pending' ? { pending_approval: true } : {}),
								})
							}
							icons={<RedoOutlined />}
						>
							SYNC
						</ButtonWrapper>
						{activeTab === 'all' && (
							<ACL action="create" module="vendor_bill">
								<ButtonWrapper
									onClick={() => navigate('/vendor-invoice-creation')}
									icons={<PlusOutlined />}
								>
									CREATE
								</ButtonWrapper>
							</ACL>
						)}
					</Flex>
				</Flex>
			</Box>
			<Box mt={3}>
				<RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={useV2API ? searchVendorBillsV2 : getListAction}
					columns={VendorInvoiceListTableConfig(activeTab !== 'pending')}
					{...(rowSelection ? { rowSelection } : {})}
				/>
			</Box>

			<BulkPaymentModal
				visible={bulkModalVisible}
				invoices={selectedInvoices}
				loading={bulkLoading}
				onClose={() => setBulkModalVisible(false)}
				onSubmit={handleBulkPaySubmit}
			/>

			<BulkPaymentUploadModal
				visible={bulkUploadModalVisible}
				onClose={() => setBulkUploadModalVisible(false)}
				onCompleted={() =>
					setpaginateConfig({
						...initialPaginateConfig,
						search_type: 'vendor_bill',
					})
				}
			/>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Bill Date</Text>
						<RangePicker
							name="date_filter"
							style={{ width: '100%' }}
							allowClear
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									// When date is cleared, set these to undefined so they are not
									// sent in the V2 search payload (old UI also omits them).
									from_date: val ? getFormattedDate(val[0]) : undefined,
									to_date: val ? getFormattedEndDate(val[1]) : undefined,
								}))
							}
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={businessUnitIds}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
								}));
								if (val.length > 0) {
									message.warning(
										'Entities without an association to a Business Unit are not visible.'
									);
								}
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
								}))
							}
							allowClear
							onClear={() => {
								setpaginateConfig((prev) => ({
									...prev,
									city: '',
									client_id: undefined,
									project_id: '',
									vendor_id: '',
									trip_type: '',
									...(activeTab === 'pending' ? { pending_approval: true } : {}),
								}));
							}}
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginateConfig?.client_name}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									client_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginateConfig?.project_location_code}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									project_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_name"
							value={paginateConfig?.vendor_name}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									vendor_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={vendorsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Payment Status</Text>
						<Select
							style={{ width: '100%' }}
							name="payment_status"
							value={paginateConfig?.payment_status || undefined}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									payment_status: val,
								}))
							}
							allowClear
							onClear={() =>
								setpaginateConfig((prev) => ({
									...prev,
									payment_status: undefined,
								}))
							}
							placeholder="Select"
							options={[
								{ value: 'pending', label: 'Pending' },
								{ value: 'paid', label: 'Paid' },
							]}
						/>
					</Box>
					<Box>
						{/* <ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() =>
								setpaginateConfig((prev) => ({
									...prev,
									user_type: 'admin',
								}))
							}
						>
							RESET FILTERS
						</ButtonWrapper> */}
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								if (activeTab === 'pending') {
									setpaginateConfig({
										...initialPaginateConfig,
										search_type: 'vendor_bill',
										pending_approval: true,
									});
								} else {
									window.location = `${window.origin}/vendor-invoices`;
								}
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
			<ACL action="generate_report" module="vendor_invoice_trips_export">
				<ExportCustomCalender
					exportModalVisible={exportModalVisible}
					setExportModalVisible={setExportModalVisible}
					onExport={exportfunc}
				/>
			</ACL>
		</>
	);
}
